/**
 * @jsxRuntime classic
 * @jsx jsx
 */
import { type RefObject, useMemo } from 'react';

import { cx, jsx } from '@compiled/react';
import { defineMessages, useIntl } from 'react-intl-next';

import { cssMap } from '@atlaskit/css';
import VideoPauseOverlayIcon from '@atlaskit/icon/core/video-pause-overlay';
import VideoPlayOverlayIcon from '@atlaskit/icon/core/video-play-overlay';
import { Pressable } from '@atlaskit/primitives/compiled';
import Spinner from '@atlaskit/spinner';
import { token } from '@atlaskit/tokens';

import { useAudioPlayerStreamingContext } from '../../../context/AudioPlayerStreamingProvider';
import { useAudioPlayerStore } from '../../../hooks/useAudioPlayerStore';
import { useAudioPlayerStreamingStore } from '../../../hooks/useAudioPlayerStreamingStore';
import { AudioStreamingState } from '../../../state/AudioPlayerStreamingStore';

const i18n = defineMessages({
	pauseButtonLabel: {
		id: 'audio.play-pause-button.pause-label',
		defaultMessage: 'Pause',
		description:
			'The label is used as a tooltip and for accessibility for the icon button which will pause the actively playing audio',
	},
	playButtonLabel: {
		id: 'audio.play-pause-button.play-label',
		defaultMessage: 'Play',
		description:
			'The label is used as a tooltip and for accessibility for the icon button which will play the actively paused audio',
	},
	loadingButtonLabel: {
		id: 'audio.play-pause-button.loading-label',
		defaultMessage: 'Loading',
		description:
			'The label is used as a tooltip and for accessibility for the icon button when the audio is loading',
	},
});

const styles = cssMap({
	button: {
		display: 'inline-flex',
		alignItems: 'center',
		justifyContent: 'center',
		width: '64px',
		height: '64px',
		borderRadius: token('radius.full'),
		paddingTop: token('space.100'),
		paddingRight: token('space.100'),
		paddingBottom: token('space.100'),
		paddingLeft: token('space.100'),
		backgroundColor: token('color.background.neutral'),
	},
	hover: {
		'&:hover': {
			backgroundColor: token('color.background.neutral.hovered'),
		},
		'&:active': {
			backgroundColor: token('color.background.neutral.pressed'),
		},
	},
	icon: {
		display: 'inline-block',
		transform: 'scale(1.25)',
	},
});

export type PlayPauseButtonProps = {
	audioRef: RefObject<HTMLAudioElement>;
};

export const PlayPauseButton = ({ audioRef }: PlayPauseButtonProps): React.ReactNode => {
	const { formatMessage } = useIntl();

	const { state: streamingState } = useAudioPlayerStreamingStore();
	const { isPlaying, pendingContent, activatePendingContent } = useAudioPlayerStore();
	const { fetchAudio } = useAudioPlayerStreamingContext();

	const isLoading = streamingState.state === AudioStreamingState.LOADING;

	const { buttonContent, testId } = useMemo(() => {
		let buttonContent: React.ReactNode;
		let testId = 'miniplayer-play';

		if (isLoading) {
			testId = 'miniplayer-loading';
			buttonContent = <Spinner label={formatMessage(i18n.loadingButtonLabel)} />;
		} else if (isPlaying && !pendingContent) {
			buttonContent = <VideoPauseOverlayIcon label={formatMessage(i18n.pauseButtonLabel)} />;
			testId = 'miniplayer-pause';
		} else {
			buttonContent = <VideoPlayOverlayIcon label={formatMessage(i18n.playButtonLabel)} />;
		}

		return { buttonContent, testId };
	}, [isLoading, isPlaying, pendingContent, formatMessage]);

	const handlePause = () => {
		if (audioRef.current) {
			audioRef.current.pause();
		}
	};

	const handlePlay = async () => {
		if (audioRef.current) {
			await audioRef.current.play();
		}
	};

	const handleClick = async () => {
		if (isPlaying && !pendingContent) {
			handlePause();
		} else if (streamingState.state === AudioStreamingState.UNINITIALIZED || pendingContent) {
			if (pendingContent) {
				if (audioRef.current) {
					audioRef.current.pause();
				}
				activatePendingContent();
			}
			fetchAudio();
		} else {
			await handlePlay();
		}
	};

	return (
		<Pressable
			onClick={handleClick}
			xcss={cx(styles.button, !isLoading && styles.hover)}
			testId={testId}
			isDisabled={isLoading}
		>
			<span css={styles.icon}>{buttonContent}</span>
		</Pressable>
	);
};
